import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const debtKeys = {
  all: ['debt'],
  coupons: (filters) => [...debtKeys.all, 'coupons', filters],
  notifications: (filters) => [...debtKeys.all, 'notifications', filters],
};

const sanitizeDebtDetailFiltersForBulk = (filters = {}) => {
  if (!filters || typeof filters !== 'object') return {};
  const { page, per_page, contract_number: contractNumber, ...rest } = filters;

  const normalized = { ...rest };
  if (contractNumber) {
    normalized.contract_number = typeof contractNumber === 'string'
      ? contractNumber.trim()
      : contractNumber;
  }

  return normalized;
};

/**
 * Hook para obtener cupones de deuda
 * @param {Object} filters - Filtros para la consulta
 */
export const useDebtCoupons = (filters = {}) => {
  return useQuery({
    queryKey: debtKeys.coupons(filters),
    queryFn: async () => {
      const cleanFilters = { ...filters };
      
      if (cleanFilters.group === '') {
        delete cleanFilters.group;
      }

      const response = await apiClient.get('debt', {
        params: cleanFilters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para obtener cupones próximos a vencer y generar notificaciones
 * @param {Object} filters - Filtros para la consulta
 */
export const useDebtNotifications = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: debtKeys.notifications(filters),
    queryFn: async () => {
      const cleanFilters = { ...filters };

      Object.keys(cleanFilters).forEach((key) => {
        if (
          cleanFilters[key] === '' ||
          cleanFilters[key] === null ||
          cleanFilters[key] === undefined
        ) {
          delete cleanFilters[key];
        }
      });

      const response = await apiClient.get('debt/notifications', {
        params: cleanFilters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

export const fetchDebtDetailBulk = async (filters = {}, { summaryOnly = false } = {}) => {
  const sanitizedFilters = sanitizeDebtDetailFiltersForBulk(filters);
  const response = await apiClient.get('debt', {
    params: {
      ...sanitizedFilters,
      ...(summaryOnly ? { summary_only: true } : { include_all: true }),
    },
  });

  const payload = response.data || {};

  return {
    data: summaryOnly ? undefined : payload.grouped_coupons || [],
    summary: payload.summary || null,
  };
};

export const fetchDebtNotificationsBulk = async (
  { fromDate, toDate },
  { perPage = 500 } = {}
) => {
  const results = [];
  let currentPage = 1;
  let totalPages = 1;

  do {
    const response = await apiClient.get('debt/notifications', {
      params: {
        from_date: fromDate,
        to_date: toDate,
        page: currentPage,
        per_page: perPage,
      },
    });

    const payload = response.data || {};
    results.push(...(payload.results || []));
    totalPages = payload.pages ?? 0;
    currentPage += 1;
  } while (totalPages && currentPage <= totalPages);

  return results;
};

/**
 * Hook para actualizar cupones de deuda
 */
export const useUpdateDebtCoupons = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dataToSend) => {
      if (!dataToSend || !dataToSend.coupons) {
        throw new Error('Payload is missing or incorrect');
      }
      const response = await apiClient.post('debt/update', dataToSend);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: debtKeys.all });
    },
    onError: (error) => {
      console.error('Error updating debt coupons:', error);
    },
  });
};

/**
 * Hook para actualizar el estado de deuda del contrato.
 */
export const useUpdateDebtStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      if (!payload?.contractUuid || !payload?.debt_status) {
        throw new Error('Payload is missing or incorrect');
      }
      const response = await apiClient.post('debt/contract-debt-status', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: debtKeys.all });
    },
    onError: (error) => {
      console.error('Error updating debt status:', error);
    },
  });
};

/**
 * Hook para cargar/reemplazar acuerdo vigente de un contrato.
 */
export const useUploadActiveAgreement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contractUuid, file }) => {
      if (!contractUuid || !file) {
        throw new Error('Payload is missing or incorrect');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `debt/contracts/${contractUuid}/active-agreements`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: debtKeys.all });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
    onError: (error) => {
      console.error('Error uploading active agreement:', error);
    },
  });
};

/**
 * Hook para enviar notificaciones de deuda
 */
export const useSendDebtNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      if (!payload) {
        throw new Error('Payload is missing or incorrect');
      }
      const response = await apiClient.post('debt/notifications/send', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: debtKeys.notifications({}) });
    },
    onError: (error) => {
      console.error('Error sending debt notifications:', error);
    },
  });
};
